'use client'

import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { ChevronLeftIcon, ChevronRightIcon, SlidersHorizontalIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useContext, useEffect, useRef, useState } from 'react'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { WIDGET_MENU_ROW_CLASS } from '@/components/widget/widget-menu-row'
import { ChatContainerContext } from '@/features/order/chat/lib/chat-container-context'
import { cn } from '@/lib/utils'

export interface WidgetSettingsSection {
  id: string
  label: string
  valueLabel?: string
  valueClassName?: string
  /** Rendered in the detail (drill-down) screen. Must not depend on a Radix
   *  DropdownMenu ancestor (e.g. a non-`inline` FilterRow submenu) — this popover
   *  provides no such context. Use FilterRow's `inline` accordion mode instead. */
  content: React.ReactNode
}

const TRANSITION_DURATION = 0.12

/**
 * Replaces the old pattern of nesting `FilterRow`'s Radix `DropdownMenuSub` flyouts
 * inside a widget's "..." menu — that stacked to 3+ levels for some widgets (Filter →
 * Instruments/Expiries/...), which hover-based flyouts handle poorly. This renders a
 * single Popover that drills down: a list of sections, and clicking one slides in that
 * section's own content with a back button, instead of opening another flyout.
 *
 * Rendered by WidgetActionsMenu (components/widget/linking-action-header.tsx), between
 * the fullscreen toggle and the "..." trigger, so structural actions (Move to Top /
 * Minimize / Duplicate / Delete) stay separate from widget configuration.
 */
export function WidgetSettingsPopover({
  sections,
  className,
}: {
  sections: WidgetSettingsSection[]
  className?: string
}) {
  const t = useTranslations('trade')
  const [open, setOpen] = useState(false)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [direction, setDirection] = useState<1 | -1>(1)
  const chatContainer = useContext(ChatContainerContext)
  const portalContainer = chatContainer?.ownerDocument.body
  const shouldReduceMotion = useReducedMotion()

  const backButtonRef = useRef<HTMLButtonElement>(null)
  const sectionRowRefs = useRef(new Map<string, HTMLButtonElement>())
  const lastActiveIdRef = useRef<string | null>(null)

  // Reset to the list view every time the popover closes, so it never reopens
  // mid-detail.
  useEffect(() => {
    if (!open) setActiveId(null)
  }, [open])

  // A section the popover was showing can disappear out from under it — e.g. the
  // caller recomputes `sections` for a different tab while the popover is still open.
  // Fall back to the list instead of showing a stale/blank detail screen.
  useEffect(() => {
    if (activeId && !sections.some((s) => s.id === activeId)) setActiveId(null)
  }, [sections, activeId])

  // Move focus with the drill-down: into the back button on the way in, back onto the
  // section row that was activated on the way out. Keyboard users would otherwise lose
  // focus into the body on every navigation.
  useEffect(() => {
    if (activeId) {
      lastActiveIdRef.current = activeId
      backButtonRef.current?.focus()
    } else if (lastActiveIdRef.current) {
      sectionRowRefs.current.get(lastActiveIdRef.current)?.focus()
      lastActiveIdRef.current = null
    }
  }, [activeId])

  if (sections.length === 0) return null

  const activeSection = sections.find((s) => s.id === activeId) ?? null

  const openSection = (id: string) => {
    setDirection(1)
    setActiveId(id)
  }
  const goBack = () => {
    setDirection(-1)
    setActiveId(null)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <motion.button
          className={cn(
            'no-drag not-disabled:hover:bg-foreground/10 data-[state=open]:bg-foreground/10 border-foreground/10 h-9 cursor-pointer p-2',
            className
          )}
          whileTap={{ scale: 0.95 }}
          aria-label={t('widgetSettings')}
        >
          <SlidersHorizontalIcon className="size-3.5 text-white/50" />
        </motion.button>
      </PopoverTrigger>
      <PopoverContent
        container={portalContainer}
        align="end"
        sideOffset={4}
        className="no-drag w-64 overflow-hidden p-0"
      >
        {/* mode="wait" so only one panel (list or detail) is ever in the DOM — without
            it, the exiting and entering panels briefly coexist as normal-flow siblings,
            stacking vertically and making the container jump/rise as the old one
            collapses away. */}
        <AnimatePresence mode="wait" initial={false} custom={direction}>
          {activeSection ? (
            <motion.div
              key={activeSection.id}
              custom={direction}
              initial={shouldReduceMotion ? false : { opacity: 0, x: 8 * direction }}
              animate={{ opacity: 1, x: 0 }}
              exit={shouldReduceMotion ? undefined : { opacity: 0, x: -8 * direction }}
              transition={{ duration: shouldReduceMotion ? 0 : TRANSITION_DURATION }}
            >
              <button
                ref={backButtonRef}
                onClick={goBack}
                className="flex w-full cursor-pointer items-center gap-1.5 border-b border-white/10 px-2 py-2 text-left text-sm font-medium text-white transition-colors hover:bg-white/5"
              >
                <ChevronLeftIcon className="size-3.5 shrink-0" />
                <span className="truncate">{activeSection.label}</span>
              </button>
              <div className="max-h-[min(420px,60dvh)] overflow-y-auto p-1.5">
                {activeSection.content}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="__list__"
              custom={direction}
              initial={shouldReduceMotion ? false : { opacity: 0, x: -8 * direction }}
              animate={{ opacity: 1, x: 0 }}
              exit={shouldReduceMotion ? undefined : { opacity: 0, x: 8 * direction }}
              transition={{ duration: shouldReduceMotion ? 0 : TRANSITION_DURATION }}
              className="flex flex-col gap-1 p-1.5"
            >
              {sections.map((section) => (
                <button
                  key={section.id}
                  ref={(el) => {
                    if (el) sectionRowRefs.current.set(section.id, el)
                    else sectionRowRefs.current.delete(section.id)
                  }}
                  onClick={() => openSection(section.id)}
                  className={WIDGET_MENU_ROW_CLASS}
                >
                  <span className="flex-1 truncate text-left">{section.label}</span>
                  {section.valueLabel && (
                    <span
                      className={cn(
                        'text-foreground/50 max-w-20 truncate text-right text-xs',
                        section.valueClassName
                      )}
                    >
                      {section.valueLabel}
                    </span>
                  )}
                  <ChevronRightIcon className="size-3.5 shrink-0 text-white/30" />
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </PopoverContent>
    </Popover>
  )
}
